import { Icon } from './Icon';
import { fmtNum } from './format';

// Shared list pagination — fixed 20 records per page, used on every list page
// (bookings, dogs & owners, …). Presentational: the page state + slicing/fetch
// live in the page; this renders the "showing X–Y of N" count + Prev/Next.
export const PAGE_SIZE = 20;

export interface ListTotals {
  bookings: number | null;
  viewings: number | null;
}

export interface PaginationProps {
  page: number; // 1-based
  // A single list's total (every list but the mixed one). null = unknown.
  total?: number | null;
  // THE MIXED LIST'S TWO FIGURES, NEVER SUMMED (viewings-in-the-bookings-list
  // AC2, AC12; decision/counting-rules.md). Page arithmetic sums them privately
  // and displays neither sum.
  totals?: ListTotals | null;
  kind?: 'all' | 'booking' | 'viewing';
  // The window handed over by Daily — named in the foot, because a figure
  // without its scope is the "54 or 55" confusion again.
  window?: { from: string; to: string } | null;
  pageSize?: number;
  onPage: (page: number) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayMonth = (iso: string): string => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
};

function figures(totals: ListTotals, kind: 'all' | 'booking' | 'viewing'): string {
  const b = totals.bookings == null ? null : `${fmtNum(totals.bookings)} bookings`;
  const v = totals.viewings == null ? null : `${fmtNum(totals.viewings)} viewings`;
  if (kind === 'viewing') return v ?? '';
  if (kind === 'booking') return b ?? '';
  return [b, v].filter(Boolean).join(' · ');
}

export function Pagination({ page, total = null, totals = null, kind, window = null, pageSize = PAGE_SIZE, onPage }: PaginationProps) {
  // The row count paging runs over — private; never shown as one number.
  const rowCount = totals ? (totals.bookings ?? 0) + (totals.viewings ?? 0) : total;
  // Two totals are ALWAYS two figures — kind or no kind (claude, PR #181): a
  // caller that forgets `kind` must not fall through to the summed branch.
  const mixed = totals != null;
  const mixedKind = kind ?? 'all';
  if (!mixed && rowCount != null && rowCount <= pageSize) return null; // single page — no control
  const pageCount = rowCount != null ? Math.max(1, Math.ceil(rowCount / pageSize)) : null;
  const from = (page - 1) * pageSize + 1;
  const to = rowCount != null ? Math.min(rowCount, page * pageSize) : page * pageSize;
  const atLast = pageCount != null && page >= pageCount;
  const singlePage = pageCount != null && pageCount <= 1;

  let count: string;
  if (mixed && window) count = `${figures(totals!, mixedKind)}, ${dayMonth(window.from)} – ${dayMonth(window.to)}`;
  else if (mixed) count = rowCount === 0 ? figures(totals!, mixedKind) : `Showing ${fmtNum(from)}–${fmtNum(to)} of ${figures(totals!, mixedKind)}`;
  else count = rowCount != null ? `Showing ${fmtNum(from)}–${fmtNum(to)} of ${fmtNum(rowCount)}` : `Showing ${fmtNum(from)}–${fmtNum(to)}`;

  return (
    <div className="pagination">
      <span className="pg-count">{count}</span>
      {!(mixed && singlePage) && (
        <div className="pg-controls">
          <button className="pg-btn" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
            <Icon name="left" size={14} /> Prev
          </button>
          <span className="pg-page">{pageCount != null ? `Page ${fmtNum(page)} of ${fmtNum(pageCount)}` : `Page ${fmtNum(page)}`}</span>
          <button className="pg-btn" onClick={() => onPage(page + 1)} disabled={atLast} aria-label="Next page">
            Next <Icon name="right" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
